import type { ErrorPolicy } from 'apollo-client';

import { graphql } from 'react-relay';

import { useQueryNoFurtherUpdateInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryNoFurtherUpdateInterop.ts';
import { useExperimentalPromotedSpacesQuery } from '@confluence/legacy-schema-bridge/entry-points/useExperimentalPromotedSpacesQuery.ts';

import type {
	useSpaceEnabledContentTypesQuery$data,
	useSpaceEnabledContentTypesQuery$variables,
} from './__generated__/useSpaceEnabledContentTypesQuery.graphql.ts';

const useSpaceEnabledContentTypesQuery = graphql`
	query useSpaceEnabledContentTypesQuery($spaceKey: String) {
		space(key: $spaceKey) {
			id
			key
			spaceTypeSettings {
				enabledContentTypes {
					isBlogsEnabled
					isDatabasesEnabled
					isEmbedsEnabled
					isLivePagesEnabled
					isSlidesEnabled
					isWhiteboardsEnabled
					isFoldersEnabled
				}
			}
		}
	}
`;

export function useSpaceEnabledContentTypes(spaceKey: string | undefined) {
	const { data: defaultSpaceKeyData, loading: isDefaultSpaceKeyLoading } =
		useExperimentalPromotedSpacesQuery({
			skip: spaceKey !== undefined,
			variables: {
				promotedSpacesLimit: 1,
				otherSpacesLimit: 1,
				promotedSpaceKey: null,
			},
		});

	const spaceEnabledContentTypesQueryOptions = {
		errorPolicy: 'all' as ErrorPolicy,
		skip: !spaceKey && !defaultSpaceKeyData,
		variables: {
			spaceKey: spaceKey || defaultSpaceKeyData?.experimentalPromotedSpaces?.nodes?.[0]?.id,
		},
	};

	const { data: enabledContentTypesData, loading: isAnyContentTypesLoading } =
		useQueryNoFurtherUpdateInterop<
			useSpaceEnabledContentTypesQuery$data,
			useSpaceEnabledContentTypesQuery$variables
		>(useSpaceEnabledContentTypesQuery, spaceEnabledContentTypesQueryOptions);

	const enabledContentTypes =
		enabledContentTypesData?.space?.spaceTypeSettings?.enabledContentTypes;
	const hasUndefinedContentTypes =
		enabledContentTypes === undefined ||
		Object.values(enabledContentTypes).some((type) => type === undefined);

	const isLoading =
		isDefaultSpaceKeyLoading || isAnyContentTypesLoading || hasUndefinedContentTypes;

	return {
		isCreateWhiteboardEnabled: Boolean(enabledContentTypes?.isWhiteboardsEnabled),
		isCreateBlogEnabled: Boolean(enabledContentTypes?.isBlogsEnabled),
		isCreateDatabaseEnabled: Boolean(enabledContentTypes?.isDatabasesEnabled),
		isCreateSmartLinkEnabled: Boolean(enabledContentTypes?.isEmbedsEnabled),
		isCreateLiveDocEnabled: Boolean(enabledContentTypes?.isLivePagesEnabled),
		isCreateFolderEnabled: Boolean(enabledContentTypes?.isFoldersEnabled),
		isCreateSlidesEnabled: Boolean(enabledContentTypes?.isSlidesEnabled),
		isLoading,
	};
}
